import {
	CompareTable,
	Conversation,
	type Figure,
	Figures,
	InShort,
	RelatedPages,
	type Turn,
} from "@marketing/components/answer";
import { Article } from "@marketing/components/article";
import {
	marketingHead,
	PageIntro,
	Section,
	Steps,
} from "@marketing/components/blocks";
import {
	type ExampleRequest,
	ExampleRequests,
} from "@marketing/components/ExampleRequests";
import { FlowDiagram } from "@marketing/components/FlowDiagram";
import { Closing } from "@marketing/home/Closing";
import { Faq } from "@marketing/home/Faq";
import { createFileRoute } from "@tanstack/react-router";

const UPDATED = "2026-11-05";

const FAQ = [
	{
		q: "Does it speak Emirati and Gulf Arabic?",
		a: "Yes. It reads Gulf Arabic as people in the UAE write it to a shop, Arabizi and English, mixed in one message if that is how they type, and answers in the language and script it was asked in. By voice it speaks Gulf Arabic with a voice chosen for your store.",
	},
	{
		q: "Can it answer in other languages?",
		a: "By voice it speaks more than 30 languages. In chat it answers in the language the shopper writes in. Before launch we test the languages your shoppers actually use on your own catalog.",
	},
	{
		q: "Can shoppers order on WhatsApp in the UAE?",
		a: "Yes. Your WhatsApp Business number answers with product cards, builds the basket and sends a checkout the shopper confirms with a tap. Shoppers can also send voice notes, which are transcribed and answered like typed messages.",
	},
	{
		q: "Does it work for quick commerce and dark stores?",
		a: "Yes. Each store or dark store keeps its own prices and stock in dirhams, and the assistant can ask where the shopper is first when prices or range differ by area, so it only offers what can be delivered.",
	},
	{
		q: "How does it handle the UAE PDPL?",
		a: "Our servers are in the European Union today, no conversation is ever used to train an AI model, credentials are encrypted and shopper data can be deleted on request. We do not claim certification; we go through your obligations under the UAE's data protection law with your team before launch.",
	},
	{
		q: "Which UAE retailers use it?",
		a: "Access is by invitation and we name retailers only with their permission. The team behind Hephon Commerce built Panda's in-app shopping assistant in Saudi Arabia, a separate build described in the Panda case study on hephon.ai.",
	},
	{
		q: "Can my store sell alcohol through it?",
		a: "In your app or on your website, a store that sells alcohol can show it. On WhatsApp, alcohol and tobacco are always excluded, for every retailer.",
	},
	{
		q: "How is it priced?",
		a: "Pricing is a proposal per retailer and depends on your stores and the channels you use. There is no public price. Book a demo and we will send you a proposal.",
	},
];

const REQUESTS: ExampleRequest[] = [
	{
		text: "أبا خبز وبيض حق الريوق",
		en: "I want bread and eggs for breakfast. (Emirati)",
	},
	{
		text: "Snacks for a desert camping trip, 8 people, under 200 dirhams",
		en: "A budget for a whole list.",
	},
	{ text: "شو عندكم عصاير طازجة؟", en: "What fresh juices do you have?" },
	{ text: "Cheapest basmati rice, 5 kg", en: "Cheapest first." },
	{ text: "3indkom laban rayeb?", en: "Do you have laban? (Arabizi)" },
	{
		text: "Machboos for 6 tonight",
		en: "A dish becomes its ingredients, in the right pack sizes.",
	},
];

const CHAT: Turn[] = [
	{
		from: "shopper",
		text: "مرحبا، أبا لبن وخبز حق الريوق",
		en: "Hi, I want laban and bread for breakfast.",
	},
	{
		from: "assistant",
		text: "هلا والله. هذا المتوفر عندنا في فرعك من اللبن والخبز:",
		en: "Welcome. Here is the laban and bread available at your branch:",
		note: "Two searches at the shopper's store, one row each, prices in dirhams.",
	},
	{
		from: "shopper",
		text: "The laban is too big, في حجم أصغر؟",
		en: "That laban is too big, is there a smaller size?",
	},
	{
		from: "assistant",
		text: "أكيد، هذي أحجام أصغر من نفس النوع.",
		en: "Sure, here are smaller sizes of the same kind.",
		note: '"Something similar" stays in the product\'s category.',
	},
	{
		from: "shopper",
		text: "Also snacks for a camping trip, 8 people, under 200 dirhams total",
	},
	{
		from: "assistant",
		text: "Here is a list for 8 that fits within 200 dirhams: crisps, nuts, juice and water.",
		note: "Written in English, so the reply is in English. One budget for the whole list: each item shows only if it fits with the cheapest of the others.",
	},
	{
		from: "shopper",
		text: "زين، حطهم كلهم",
		en: "Good, add them all.",
	},
	{
		from: "assistant",
		text: "تم. السلة جاهزة، راجعها واضغط تأكيد الطلب.",
		en: "Done. Your basket is ready: review it and tap confirm.",
		note: "Nothing is ordered until the shopper confirms.",
	},
];

const FIGURES: Figure[] = [
	{
		value: "30+",
		label: "Languages it speaks by voice",
		source: "Hephon Commerce voice engines, November 2026.",
	},
	{
		value: "3 channels",
		label: "Your app, your WhatsApp number and voice, one basket",
		source: "Hephon Commerce product, November 2026.",
	},
	{
		value: "15,000",
		label: "Products in the public demo store you can test",
		source: "Hephon Market at commerce.hephon.ai/c/demo, a real quick-commerce catalog under a neutral name. It understands Gulf Arabic; prices are in US dollars.",
	},
];

const FORMATS = [
	[
		"Hypermarkets",
		"Large range, many departments, branches with different stock",
		"Search that never defaults to food, prices and stock per branch",
	],
	[
		"Co-operatives",
		"Members across one emirate, families shopping by phone and WhatsApp",
		"Gulf Arabic replies from a phrasebook your team approves, voice notes",
	],
	[
		"Quick commerce and dark stores",
		"Range and price depend on the nearest dark store",
		"Asks where the shopper is first, then offers only what that store has",
	],
	[
		"Speciality and online grocers",
		"Smaller catalogs, often on Shopify or WooCommerce",
		"Connects in days, recipes turned into baskets with pack sizes",
	],
];

const STEPS = [
	{
		title: "Connect your catalog",
		text: "From Shopify, WooCommerce, a product feed, our REST API or a custom connector, with each store's prices in dirhams and its stock.",
	},
	{
		title: "Set it up and test it",
		text: "Its name, its tone in Arabic and English, the products you never sell, and handover. Then a test suite built from your catalog runs in your shoppers' languages.",
	},
	{
		title: "Go live channel by channel",
		text: "Start on WhatsApp or in your app, and add voice ordering when you are ready.",
	},
];

const RELATED = [
	{ href: "/security/pdpl", label: "UAE PDPL and your data" },
	{ href: "/quick-commerce", label: "For quick commerce" },
	{ href: "/channels/whatsapp", label: "WhatsApp ordering" },
	{ href: "/channels/voice", label: "Voice ordering" },
	{
		href: "/arabic-chatbot-for-ecommerce",
		label: "Arabic chatbot for ecommerce",
	},
	{ href: "/saudi-arabia", label: "For Saudi Arabia" },
];

export const Route = createFileRoute("/_marketing/uae")({
	head: () =>
		marketingHead({
			title: "AI Shopping Assistant for the UAE | Hephon Commerce",
			description:
				"AI shopping assistant for the UAE: an Arabic chatbot for supermarkets and quick commerce that reads Gulf Arabic and English, with dirham prices per branch.",
			path: "/uae",
			breadcrumb: ["United Arab Emirates"],
			faq: FAQ,
			updated: UPDATED,
		}),
	component: UaePage,
});

function UaePage() {
	return (
		<>
			<PageIntro
				kicker="United Arab Emirates"
				title="The AI shopping assistant for supermarkets in the UAE"
				lead="For hypermarkets, co-ops and quick commerce: it understands shoppers in Gulf Arabic, English and Arabizi, searches your catalog with each store's prices in dirhams, and fills the basket in your app, on WhatsApp and by voice."
			>
				<ExampleRequests
					items={REQUESTS}
					className="mx-auto max-w-4xl pb-10 text-start sm:pb-14"
				/>
			</PageIntro>

			<InShort updated={UPDATED}>
				<p>
					Hephon Commerce is an AI shopping assistant for
					hypermarkets, co-operatives, supermarkets and quick-commerce
					retailers in the UAE. It reads Emirati and Gulf Arabic,
					English and Arabizi, mixed in one message, and speaks more
					than 30 languages by voice.
				</p>
				<p>
					It searches the retailer's live catalog with each branch's
					or dark store's prices in AED and fills the basket in the
					retailer's own app, on its WhatsApp Business number and by
					voice. Nothing is ordered until the shopper confirms.
				</p>
			</InShort>

			<Section
				id="chat-title"
				tone="mist"
				title="A UAE shopper's chat, in two languages."
				lead="Emirati Arabic and English in the same conversation, as people in the UAE text a shop."
				center
			>
				<Conversation
					turns={CHAT}
					caption="A sample conversation written for this page to show the behaviour. It is not a transcript of a real shopper."
				/>
			</Section>

			<Article>
				<h2 id="dialect">
					Does it understand Emirati Arabic and English mixed?
				</h2>
				<p>
					Yes. In the UAE one shopper can write أبا (I want), حق (for)
					and الريوق (breakfast) in Arabic, then switch to English for
					the next line, then type Arabizi such as "3indkom laban?" on
					the way home. A chatbot that expects one language per
					conversation gets this wrong. Hephon Commerce weighs each
					message as a whole and answers in the language and script it
					was written in. A message that could be either, such as a
					brand name alone, keeps the language the conversation was
					already in.
				</p>
				<p>
					Its Gulf Arabic replies come from a phrasebook of approved
					phrases for each situation, which your team can edit, so the
					tone sounds local rather than translated. Product search
					combines keywords and meaning over your live catalog, in
					Arabic script, Arabizi and English, and brands spelled
					several ways are still found.
				</p>

				<h2 id="languages">
					What about shoppers who speak other languages?
				</h2>
				<p>
					Most UAE residents are not Arabic speakers, and many shop in
					English. Hephon Commerce chats in English and Arabic first,
					and in French too. By voice it speaks more than 30
					languages, so a shopper can order in the language they are
					most at ease in. Which languages matter depends on your
					shoppers, so before launch we test the ones you name on your
					own catalog, with messages generated from your products.
				</p>

				<h2 id="formats">
					Does it work for co-ops, hypermarkets and quick commerce?
				</h2>
				<p>
					Yes. Every branch or dark store is its own store in Hephon
					Commerce, with its own prices and stock in dirhams. Where
					range or price depends on the area, as in quick commerce,
					the assistant can ask where the shopper is first and offer
					only what the nearest store can deliver. The table below
					shows how it fits each format.
				</p>
			</Article>

			<Section
				id="formats-title"
				tone="mist"
				title="How it fits each UAE retail format."
			>
				<CompareTable
					columns={[
						"Format",
						"What shoppers deal with",
						"What the assistant does",
					]}
					rows={FORMATS}
				/>
			</Section>

			<Article>
				<h2 id="whatsapp">
					Can shoppers order on WhatsApp in the UAE?
				</h2>
				<p>
					Yes. It answers on your own WhatsApp Business number with
					product cards, a basket, and a checkout the shopper confirms
					with a tap. Voice notes are transcribed and answered like
					typed messages. On WhatsApp, alcohol and tobacco are always
					excluded, whatever the store sells elsewhere.
				</p>

				<h2 id="voice">Can shoppers order by voice?</h2>
				<p>
					Yes, with live voice inside your app, voice notes on
					WhatsApp, and calls. On a call it speaks Gulf Arabic with a
					voice chosen for your store, reads the basket back, and the
					order is placed only when the caller presses 1. Calling
					rules in the UAE depend on the channel and the line, so we
					agree with your team how calls reach the assistant before
					launch.
				</p>

				<h2 id="search">How does it find the right products?</h2>
				<p>
					Before any product is shown, a fast classifier checks the
					top search results against the shopper's actual message and
					drops what does not match. It understands budgets ("under
					200 dirhams" for a whole list), "cheapest first", "more
					options", "something similar" and follow-ups such as "any of
					these on offer?". It never assumes food, so an electronics
					shelf is searched like any other. A dish and a number of
					people, such as machboos for six, becomes ingredient lines
					with the packs that cover each amount for the least money.
				</p>

				<h2 id="pdpl">How does it handle data and the UAE PDPL?</h2>
				<p>
					The UAE's Federal Decree-Law No. 45 of 2021 on the
					protection of personal data, often called the UAE PDPL, sets
					rules for how shoppers' data is processed and transferred.
					Here is what Hephon Commerce does today:
				</p>
				<ul>
					<li>
						No conversation is ever used to train an AI model. Every
						model request is sent with data collection denied.
					</li>
					<li>Our servers are in the European Union today.</li>
					<li>Platform credentials are encrypted.</li>
					<li>Shopper data can be deleted on request.</li>
				</ul>
				<p>
					We do not claim compliance or any ISO or SOC certification.
					We go through your requirements with your team before
					launch. The <a href="/security/pdpl">PDPL page</a> has the
					detail.
				</p>

				<h2 id="does-not">What it does not do</h2>
				<ul>
					<li>
						It never orders on its own. The shopper confirms with a
						tap, on a signed checkout page, or with keypad 1 after a
						call's read-back.
					</li>
					<li>
						It does not invent products, prices or delivery times.
						It offers what your catalog and the shopper's store
						have.
					</li>
					<li>
						It does not rely on the prompt for the products you
						never sell. Alcohol, pork or tobacco are removed on the
						server, and a product not yet checked stays hidden.
					</li>
				</ul>

				<h2 id="not-fit">When Hephon Commerce is not the right fit</h2>
				<ul>
					<li>
						You have no online catalog. The assistant needs your
						products, prices and stock.
					</li>
					<li>
						You only need customer service in Arabic and English,
						such as order status and returns. A customer-service
						platform fits that better.
					</li>
					<li>
						You need data hosted in the UAE from day one. Our
						servers are in the European Union today.
					</li>
					<li>
						You want to sell alcohol on WhatsApp. It is always
						excluded there.
					</li>
					<li>
						You want self-serve signup and a public price. Access is
						by invitation and each retailer gets a proposal.
					</li>
				</ul>
			</Article>

			<Section
				id="numbers-title"
				tone="mist"
				title="What the numbers say."
				lead="Only figures we can stand behind, each with its source."
			>
				<Figures items={FIGURES} />
			</Section>

			<Section
				id="fits-title"
				title="Your catalog in, orders back to your platform."
				center
			>
				<FlowDiagram />
			</Section>

			<Section
				id="steps-title"
				tone="mist"
				title="Live in three steps."
				center
			>
				<Steps items={STEPS} />
			</Section>

			<Section id="team-title" title="Your team stays in the loop.">
				<p className="mk-reveal max-w-3xl text-(--mk-ink-2) text-[17px] leading-relaxed">
					Every conversation, typed, voice note or call with its
					transcript, lands in one live inbox. A shopper can ask for a
					person at any time and your team takes over the same chat.
					Handover to your help desk is possible by integration.
				</p>
			</Section>

			<Faq items={FAQ} title="UAE retailers ask." />
			<RelatedPages links={RELATED} />
			<Closing />
		</>
	);
}
